import React, { useState } from 'react';
import { CheckCircle2, ClipboardCheck, FileSearch, Send, Stamp } from 'lucide-react';
import SEO from './SEO';
import { SitePage, PageHero, SectionHead, CtaBand } from './site/ui';
import { BRAND, unsplash } from './site/brand';
import { submitEnquiry } from './site/api';
import HoneyPot from './site/HoneyPot';

// Entry rules for Indian passport holders (tourism). Checked October 2026 — rules change,
// so the page tells visitors to confirm with our visa desk before travel.
const COUNTRIES = [
  { name: 'Dubai (UAE)', rule: 'Visa required', type: 'Tourist visa', docs: ['Passport valid 6+ months', 'Photo (white background)', 'Return flight & hotel booking', 'Bank statement (3 months)'] },
  { name: 'Schengen (Europe)', rule: 'Visa required', type: 'Schengen tourist visa', docs: ['Passport valid 3+ months beyond return', 'VFS appointment & biometrics', 'Travel insurance (€30,000 cover)', 'ITR, bank statements & leave letter'] },
  { name: 'USA', rule: 'Visa required', type: 'B1/B2 visitor visa', docs: ['DS-160 form', 'Interview appointment', 'Proof of ties to India', 'Financial documents'] },
  { name: 'UK', rule: 'Visa required', type: 'Standard visitor visa', docs: ['Online application', 'Biometrics at VFS', 'Bank statements & ITR', 'Employment proof'] },
  { name: 'Australia', rule: 'Visa required (online)', type: 'Visitor visa (subclass 600)', docs: ['Online lodgement', 'Financial capacity', 'Health & character', 'Itinerary & bookings'] },
  { name: 'Singapore', rule: 'Visa required', type: 'Tourist e-visa via agent', docs: ['Passport scan', 'Photo', 'Flight & hotel booking', 'Form 14A'] },
  { name: 'South Korea', rule: 'Visa required', type: 'Tourist visa', docs: ['Application form', 'Bank statement', 'ITR', 'Itinerary'] },
  { name: 'Japan', rule: 'eVisa / visa via VFS', type: 'Tourist visa', docs: ['Application form', 'Itinerary', 'Bank statement', 'ITR'] },
  { name: 'Vietnam', rule: 'eVisa', type: 'e-Visa (up to 90 days)', docs: ['Passport scan', 'Photo', 'Entry & exit details'] },
  { name: 'Egypt', rule: 'eVisa', type: 'e-Visa (30 days)', docs: ['Passport scan', 'Hotel booking', 'Return ticket'] },
  { name: 'Jordan', rule: 'eVisa / visa on arrival', type: '30 days', docs: ['Passport valid 6+ months', 'Return ticket', 'Hotel booking'] },
  { name: 'Azerbaijan', rule: 'eVisa', type: 'e-Visa (30 days)', docs: ['Passport scan', 'Hotel booking'] },
  { name: 'Georgia', rule: 'eVisa', type: 'e-Visa (30 days)', docs: ['Passport scan', 'Hotel booking', 'Travel insurance'] },
  { name: 'Russia', rule: 'eVisa', type: 'e-Visa (apply 40–4 days before entry)', docs: ['Passport scan', 'Photo', 'Travel insurance'] },
  { name: 'Indonesia (Bali)', rule: 'Visa on arrival / e‑VOA', type: '30 days, fee payable', docs: ['Passport valid 6+ months', 'Return ticket'] },
  { name: 'Sri Lanka', rule: 'Free ETA / visa on arrival', type: '30 days', docs: ['Online ETA', 'Return ticket'] },
  { name: 'Thailand', rule: 'Visa not required', type: 'Up to 30 days', docs: ['Passport valid 6+ months', 'Return ticket', 'Hotel booking'] },
  { name: 'Malaysia', rule: 'Visa not required', type: 'Up to 30 days (till 31 Dec 2026)', docs: ['Passport valid 6+ months', 'Malaysia Digital Arrival Card'] },
  { name: 'Maldives', rule: 'Visa not required', type: 'Up to 90 days', docs: ['Passport valid 6+ months', 'Confirmed resort booking', 'IMUGA declaration'] },
  { name: 'Mauritius', rule: 'Visa not required', type: 'Up to 90 days', docs: ['Passport valid 6+ months', 'Return ticket', 'Hotel booking'] },
  { name: 'Nepal', rule: 'No visa needed', type: 'Passport or Voter ID', docs: ['Valid passport or Voter ID'] },
  { name: 'Bhutan', rule: 'Entry permit', type: 'Permit beyond border areas', docs: ['Passport or Voter ID', 'Permit arranged with your tour'] },
];

const tone = (rule) => (/not required|no visa/i.test(rule) ? 'free' : /evisa|arrival|eta|permit/i.test(rule) ? 'easy' : 'full');

const EMPTY = { name: '', phone: '', email: '', country: '', date: '', travelers: '', message: '' };

const VisaAssistance = () => {
  const [open, setOpen] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState({ busy: false, ok: false, err: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, ok: false, err: '' });
    try {
      await submitEnquiry({
        name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim(), destination: f.country,
        travelDate: f.date, travelers: f.travelers, message: `Visa enquiry: ${f.country}\n${f.message}`, productType: 'visa',
      });
      setState({ busy: false, ok: true, err: '' });
      setF(EMPTY);
    } catch {
      setState({ busy: false, ok: false, err: `Sorry, that didn't go through. Please WhatsApp or call ${BRAND.phone}.` });
    }
  };

  return (
    <SitePage>
      <SEO title="Visa Assistance for Indian Travellers" description="Tourist visa help for Dubai, Schengen, USA, UK, Australia, Singapore and more — document checks and filing by the VK Tours visa desk." path="/travel-services/visa-assistance" />
      <PageHero
        image={unsplash('photo-1530469641172-8ac15d0a7d6a', 2000)}
        imageAlt="Aircraft wing above clouds"
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Services' }, { label: 'Visa assistance' }]}
        kicker="Visa desk"
        title="Visas,"
        accent="sorted"
        lede="Tourist visas for Dubai, Europe, the USA, UK, Australia and more — we check your documents, file the application and track it until it’s approved."
      >
        <a className="vk-btn vk-btn--hot" href="#visa-form"><Send size={18} /> Start a visa enquiry</a>
        <a className="vk-btn vk-btn--ghost" href={BRAND.phoneLink}>{BRAND.phone}</a>
      </PageHero>

      <section className="vk-section">
        <div className="vk-wrap">
          <SectionHead kicker="How it works" title="Three simple steps" />
          <div className="vk-feats" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            <div className="vk-feat"><FileSearch size={28} /><h3>1. Checklist</h3><p>We send the exact document list for your visa and profile.</p></div>
            <div className="vk-feat"><ClipboardCheck size={28} /><h3>2. Review</h3><p>Our team reviews every document before anything is filed.</p></div>
            <div className="vk-feat"><Stamp size={28} /><h3>3. Filing & tracking</h3><p>We file, book appointments where needed and update you until the decision.</p></div>
          </div>
        </div>
      </section>

      <section className="vk-section vk-section--sand">
        <div className="vk-wrap">
          <SectionHead kicker="For Indian passport holders" title="Entry rules by country" />
          <div className="vk-visa">
            {COUNTRIES.map((c) => (
              <div key={c.name} className={`vk-visa__item ${open === c.name ? 'is-open' : ''}`}>
                <button type="button" className="vk-visa__head" aria-expanded={open === c.name} onClick={() => setOpen(open === c.name ? null : c.name)}>
                  <span className="vk-visa__name">{c.name}</span>
                  <span className={`vk-visa__rule vk-visa__rule--${tone(c.rule)}`}>{c.rule}</span>
                </button>
                {open === c.name && (
                  <div className="vk-visa__body">
                    <p><b>{c.type}</b></p>
                    <ul className="vk-ticks">{c.docs.map((d) => <li key={d}><CheckCircle2 size={16} color="#1a9a4b" /> {d}</li>)}</ul>
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="vk-form__note" style={{ marginTop: 16 }}>Tourism rules for Indian passports as of October 2026. Rules and fees change often — our visa desk confirms the latest requirements before you apply.</p>
        </div>
      </section>

      <section className="vk-section" id="visa-form" style={{ scrollMarginTop: 'calc(var(--header-h) + 12px)' }}>
        <div className="vk-wrap vk-split vk-split--form">
          <div className="vk-panel" style={{ padding: 28 }}>
            <p className="vk-kicker">Visa enquiry</p>
            <h2 className="vk-display" style={{ fontSize: 'clamp(30px,4vw,44px)', margin: '0 0 20px' }}>Tell us where you’re going</h2>
            {state.ok ? (
              <div className="vk-alert vk-alert--ok" role="status">Thank you — our visa desk will contact you with the checklist.</div>
            ) : (
              <form className="vk-form" onSubmit={submit}><HoneyPot />
                <div className="vk-form__row">
                  <div className="vk-field"><label htmlFor="v-name">Full name *</label><input id="v-name" required value={f.name} onChange={set('name')} autoComplete="name" /></div>
                  <div className="vk-field"><label htmlFor="v-phone">Phone *</label><input id="v-phone" required type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" /></div>
                </div>
                <div className="vk-form__row">
                  <div className="vk-field"><label htmlFor="v-email">Email *</label><input id="v-email" required type="email" value={f.email} onChange={set('email')} autoComplete="email" /></div>
                  <div className="vk-field">
                    <label htmlFor="v-country">Country *</label>
                    <select id="v-country" required value={f.country} onChange={set('country')}>
                      <option value="">Select a country</option>
                      {COUNTRIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="vk-form__row">
                  <div className="vk-field"><label htmlFor="v-date">Travel date</label><input id="v-date" type="date" min={new Date().toISOString().split('T')[0]} value={f.date} onChange={set('date')} /></div>
                  <div className="vk-field"><label htmlFor="v-trav">Applicants</label><input id="v-trav" type="number" min="1" max="50" value={f.travelers} onChange={set('travelers')} /></div>
                </div>
                <div className="vk-field"><label htmlFor="v-msg">Notes</label><textarea id="v-msg" rows={3} value={f.message} onChange={set('message')} placeholder="Purpose of travel, previous visas, any refusals…" /></div>
                {state.err ? <div className="vk-alert vk-alert--err" role="alert">{state.err}</div> : null}
                <button type="submit" className="vk-btn vk-btn--hot" disabled={state.busy}>{state.busy ? 'Sending…' : 'Send visa enquiry'}</button>
              </form>
            )}
          </div>
          <div className="vk-panel vk-panel--sand">
            <h3>Why use our visa desk</h3>
            <ul className="vk-ticks">
              <li><CheckCircle2 size={18} color="var(--hot)" /> Document review before filing reduces avoidable refusals</li>
              <li><CheckCircle2 size={18} color="var(--hot)" /> Appointment booking at VFS / embassies</li>
              <li><CheckCircle2 size={18} color="var(--hot)" /> Flight and hotel bookings that match your application</li>
              <li><CheckCircle2 size={18} color="var(--hot)" /> Australia visitor visas lodged online by our team</li>
            </ul>
            <p className="vk-form__note" style={{ marginTop: 16 }}>Visa decisions are made solely by the embassy or immigration authority.</p>
          </div>
        </div>
      </section>

      <CtaBand title="Visa done?" accent="Let's plan the trip." />
    </SitePage>
  );
};

export default VisaAssistance;
